import { FC, ReactElement, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Tooltip,
  Fade,
} from "@mui/material";
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  CheckCircle,
} from "@mui/icons-material";

import DutiesStackedBar from "./duties-stacked-bar";
import { formatNumberWithSuffix, numberWithCommas } from "@/utils";
import { responsiveFontSizes, spacing } from "@/utils/responsive";
import { chartColors } from "@/constants";
import { tokens } from "@/tokens";
import { useGetSystemSettingsQuery } from "@/graphql/system-settings";

const IEEPA_REFUND_TOOL_PATH = "/tools/ieepa-refund-cape";

// TODO: re-enable CAPE Certified + CAPE Pending row beneath the Paid/Owed
// strip once the Pending amount reconciles with the IEEPA Refund CAPE tool
// for all clients. Current glitch: dashboard's CAPE Pending dollar figure
// does not match `pendingProjectedRefund` from `useCapeAnalytics` for some
// books — even after gating on `claimStatus ~ /accepted/i` AND Phase-1
// in-window via `evaluateCapePhase1Eligibility`, the figures drift on
// books with mixed liquidation dates / ACE export quirks. Resolver +
// schema + RTK hook (`useGetCapeRefundSummaryQuery`) remain in place;
// only the DutiesCard render path is disabled here. To re-enable:
//   1. Restore `useGetCapeRefundSummaryQuery` import above and the
//      `useGetCapeRefundSummaryQuery(...)` call below.
//   2. Restore the CAPE row JSX beneath the Paid/Owed legend strip.
//   3. Verify against the IEEPA Refund CAPE tool's hero numbers.

const isIeepaAuthority = (authority: string): boolean =>
  authority.toUpperCase().includes("IEEPA");

interface DutyCodeBreakdown {
  code: string;
  columnName: string;
  totalDuty: number;
  entryCount: number;
}

interface DutyAuthorityTotal {
  authority: string;
  totalDuty: number;
  entryCount: number;
  codes: DutyCodeBreakdown[];
}

interface DutyAnalyticsData {
  data?: {
    grandTotal: number;
    authorities: DutyAuthorityTotal[];
    usedLegacyFallback?: boolean;
  };
}

interface DutiesCardProps {
  data?: DutyAnalyticsData;
  isLoading?: boolean;
  isFetching?: boolean;
  totalDutiesPaid?: number;
}

// Authority display names mapping
const AUTHORITY_DISPLAY_NAMES: Record<string, string> = {
  MFN: "MFN Duty",
  SECTION_122: "Section 122",
  SECTION_201: "Section 201",
  SECTION_232: "Section 232",
  SECTION_301: "Section 301",
  "IEEPA-Reciprocal": "IEEPA Reciprocal",
  "IEEPA-Fent": "IEEPA Fentanyl",
  "IEEPA-Russian-Oil": "IEEPA Russian Oil",
  "IEEPA-Brazil": "IEEPA Brazil",
  IEEPA: "IEEPA",
  MTB: "MTB",
  AD: "Anti-Dumping",
  CVD: "Countervailing",
  OTHER: "Other Duties",
  UNKNOWN: "Unclassified",
};

type DrillDownState = null | { type: "authority"; authority: string };

const DutiesCard: FC<DutiesCardProps> = ({
  data,
  isLoading = false,
  isFetching = false,
  totalDutiesPaid = 0,
}): ReactElement => {
  const navigate = useNavigate();
  const [drillDown, setDrillDown] = useState<DrillDownState>(null);
  const hasData = data?.data !== undefined;

  // Feature flag: IEEPA Refund Alert (system-wide setting)
  const { data: systemSettings } = useGetSystemSettingsQuery();
  const showIeepaRefundAlert = systemSettings?.ieepaRefundAlert ?? false;

  const authorities = useMemo(() => {
    return (data?.data?.authorities || []).filter((auth) => auth.totalDuty > 0);
  }, [data]);

  const grandTotal = useMemo(() => {
    return data?.data?.grandTotal || 0;
  }, [data]);

  const usedLegacyFallback = data?.data?.usedLegacyFallback ?? false;

  // Total IEEPA duties for the refund alert banner
  const ieepaTotal = useMemo(() => {
    if (!showIeepaRefundAlert) return 0;
    return authorities
      .filter((auth) => isIeepaAuthority(auth.authority))
      .reduce((sum, auth) => sum + auth.totalDuty, 0);
  }, [authorities, showIeepaRefundAlert]);

  const expandedData = useMemo(() => {
    if (!drillDown || drillDown.type !== "authority") return null;
    return authorities.find((auth) => auth.authority === drillDown.authority);
  }, [drillDown, authorities]);

  // Memoize color index for expanded authority to avoid repeated findIndex calls
  const expandedColorIndex = useMemo(() => {
    if (!drillDown || drillDown.type !== "authority") return 0;
    const idx = authorities.findIndex((a) => a.authority === drillDown.authority);
    return idx >= 0 ? idx % chartColors.length : 0;
  }, [drillDown, authorities]);

  // Reset drill-down state when data changes (client switch, date change, etc.)
  useEffect(() => {
    setDrillDown(null);
  }, [data]);

  const handleToggleExpand = (authority: string) => {
    setDrillDown(
      drillDown?.type === "authority" && drillDown.authority === authority
        ? null
        : { type: "authority", authority }
    );
  };

  const handleClose = () => {
    setDrillDown(null);
  };

  const goToRefundTool = () => {
    navigate(IEEPA_REFUND_TOOL_PATH);
  };

  // Show full-card loading only on initial load (or when we have no data yet).
  // Allow background refetches to keep content visible and use the small inline spinner.
  if (isLoading || (!hasData && isFetching)) {
    return (
      <Card
        sx={{
          p: { xs: 1, md: 1.25 },
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${tokens.border.default}`,
          boxShadow: "none",
        }}
      >
        <CircularProgress size={40} />
      </Card>
    );
  }

  const isExpanded = drillDown !== null;

  return (
    <Card
      sx={{
        px: { xs: 1.5, md: 2 },
        py: { xs: 1, md: 1.25 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${tokens.border.default}`,
        boxShadow: "none",
      }}
    >
      {/* Expanded View - Animates over the header */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: tokens.bg.surface,
          zIndex: isExpanded ? 10 : -1,
          opacity: isExpanded ? 1 : 0,
          transform: isExpanded ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          px: { xs: 1.5, md: 2 },
          py: { xs: 1, md: 1.25 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Authority drill-down */}
        {expandedData && drillDown?.type === "authority" && (
          <>
            {/* Expanded Header */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  bgcolor: "rgba(0,0,0,0.05)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
                }}
              >
                <KeyboardArrowLeft />
              </IconButton>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "3px",
                  bgcolor: chartColors[expandedColorIndex],
                  flexShrink: 0,
                }}
              />
              <Tooltip title={AUTHORITY_DISPLAY_NAMES[expandedData.authority] || expandedData.authority} arrow placement="top">
                <Typography variant="h6" fontWeight={tokens.fontWeight.bold} noWrap sx={{ flex: 1 }}>
                  {AUTHORITY_DISPLAY_NAMES[expandedData.authority] || expandedData.authority}
                </Typography>
              </Tooltip>
              {showIeepaRefundAlert && isIeepaAuthority(expandedData.authority) && (
                <Tooltip
                  title="Open IEEPA Refund tool"
                  arrow
                  placement="top"
                >
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      goToRefundTool();
                    }}
                    sx={{
                      fontSize: 10,
                      px: 0.75,
                      py: 0.25,
                      bgcolor: "#e8f5e9",
                      color: "#2e7d32",
                      borderRadius: 1,
                      fontWeight: tokens.fontWeight.bold,
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      border: "1px solid #a5d6a7",
                      transition: "all 0.15s",
                      "&:hover": {
                        bgcolor: "#c8e6c9",
                        borderColor: "#81c784",
                      },
                    }}
                  >
                    Refund opportunity
                  </Box>
                </Tooltip>
              )}
              <Typography variant="h6" fontWeight={tokens.fontWeight.heavy} sx={{ color: tokens.brand.primary }}>
                ${formatNumberWithSuffix(expandedData.totalDuty)}
              </Typography>
            </Stack>

            {/* Code List */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                maxHeight: 220,
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "rgba(0,0,0,0.15)",
                  borderRadius: 2,
                },
              }}
            >
              <Stack spacing={0.5}>
                {expandedData.codes.map((code, idx) => (
                    <Fade in key={code.code} style={{ transitionDelay: `${idx * 30}ms` }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          py: 0.5,
                          px: 0.75,
                          borderRadius: 1,
                          transition: "all 0.15s",
                          "&:hover": {
                            bgcolor: "rgba(0,0,0,0.03)",
                          },
                        }}
                      >
                        {/* Color bar indicator */}
                        <Box
                          sx={{
                            width: 3,
                            height: 28,
                            borderRadius: 1,
                            bgcolor: chartColors[expandedColorIndex],
                            flexShrink: 0,
                          }}
                        />
                        {/* Code pill */}
                        <Typography
                          fontFamily="monospace"
                          fontWeight={tokens.fontWeight.bold}
                          sx={{
                            fontSize: 11,
                            bgcolor: "rgba(0,0,0,0.06)",
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 0.75,
                            flexShrink: 0,
                          }}
                        >
                          {code.code}
                        </Typography>
                        {/* Column name - flex grow */}
                        <Tooltip title={code.columnName} arrow placement="top">
                          <Typography
                            variant="caption"
                            sx={{
                              color: tokens.text.secondary,
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: 11,
                            }}
                          >
                            {code.columnName}
                          </Typography>
                        </Tooltip>
                        {/* Entry count */}
                        <Typography
                          variant="caption"
                          sx={{ color: tokens.text.secondary, fontSize: 10, flexShrink: 0 }}
                        >
                          {code.entryCount}
                        </Typography>
                        {/* Amount */}
                        <Typography
                          variant="body2"
                          fontWeight={tokens.fontWeight.heavy}
                          sx={{ minWidth: 65, textAlign: "right", fontSize: tokens.fontSize.base }}
                        >
                          ${formatNumberWithSuffix(code.totalDuty)}
                        </Typography>
                      </Stack>
                    </Fade>
                ))}
              </Stack>
            </Box>
          </>
        )}

      </Box>

      {/* Main View - Header & List */}
      <Box
        sx={{
          opacity: isExpanded ? 0 : 1,
          transform: isExpanded ? "scale(0.95)" : "scale(1)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          pointerEvents: isExpanded ? "none" : "auto",
        }}
      >
        <Box mb={spacing.md}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  sx={{ color: tokens.text.secondary }}
                  variant="body1"
                  fontWeight={tokens.fontWeight.semibold}
                >
                  Duties by Authority
                </Typography>
                {usedLegacyFallback && (
                  <Tooltip title="Using aggregated data. Re-upload trade data for detailed code breakdown.">
                    <Box
                      sx={{
                        fontSize: 10,
                        px: 0.75,
                        py: 0.25,
                        bgcolor: tokens.status.warningBg,
                        color: tokens.status.warning,
                        borderRadius: 1,
                        fontWeight: tokens.fontWeight.bold,
                      }}
                    >
                      LEGACY
                    </Box>
                  </Tooltip>
                )}
              </Stack>
              <Typography
                fontWeight={tokens.fontWeight.heavy}
                variant="h2"
                sx={{
                  color: tokens.text.primary,
                  fontSize: responsiveFontSizes["3xl"],
                  lineHeight: 1.1,
                  mt: spacing.sm,
                }}
              >
                ${numberWithCommas(grandTotal)}
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              {isFetching && <CircularProgress size={16} />}
              {showIeepaRefundAlert && ieepaTotal > 0 && (
                <Tooltip
                  title="Open IEEPA Refund tool"
                  arrow
                  placement="top"
                >
                  <Box
                    onClick={goToRefundTool}
                    sx={{
                      px: 0.75,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: "#e8f5e9",
                      border: "1px solid #a5d6a7",
                      cursor: "pointer",
                      textAlign: "right",
                      transition: "all 0.15s",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      "&:hover": {
                        bgcolor: "#c8e6c9",
                        borderColor: "#81c784",
                      },
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 9, color: "#2e7d32", fontWeight: tokens.fontWeight.bold, lineHeight: 1.2 }}>
                        Refund opportunity
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "#2e7d32", fontWeight: tokens.fontWeight.heavy, lineHeight: 1.2 }}>
                        ${numberWithCommas(ieepaTotal)}
                      </Typography>
                    </Box>
                    <KeyboardArrowRight sx={{ fontSize: 16, color: "#2e7d32" }} />
                  </Box>
                </Tooltip>
              )}
            </Stack>
          </Stack>
          <Box mt={spacing.md}>
            <DutiesStackedBar dutiesPaid={totalDutiesPaid} dutiesDue={grandTotal} />
            <Stack direction="row" spacing={{ xs: 1.5, md: 2 }} alignItems="center">
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: { xs: 8, md: 10 },
                    height: { xs: 8, md: 10 },
                    bgcolor: tokens.status.success,
                    borderRadius: "2px",
                  }}
                />
                <Typography variant="caption" fontWeight={tokens.fontWeight.semibold}>
                  Paid
                </Typography>
                <Typography variant="caption" fontWeight={tokens.fontWeight.bold}>
                  ${numberWithCommas(totalDutiesPaid)}
                </Typography>
              </Stack>
              {grandTotal - totalDutiesPaid > 0 ? (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box
                    sx={{
                      width: { xs: 8, md: 10 },
                      height: { xs: 8, md: 10 },
                      bgcolor: tokens.status.danger,
                      borderRadius: "2px",
                    }}
                  />
                  <Typography variant="caption" fontWeight={tokens.fontWeight.semibold} color={tokens.status.danger}>
                    Owed
                  </Typography>
                  <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.status.danger}>
                    ${numberWithCommas(grandTotal - totalDutiesPaid)}
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <CheckCircle sx={{ fontSize: 14, color: tokens.status.success }} />
                  <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.status.success}>
                    All paid
                  </Typography>
                </Stack>
              )}
            </Stack>
            {/* CAPE Certified + CAPE Pending row temporarily removed —
                 see top-of-file TODO. Wiring (`useGetCapeRefundSummaryQuery`,
                 backend resolver, GraphQL schema) is intentionally retained. */}
          </Box>
        </Box>

        {/* Authority List */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack spacing={0} sx={{ borderTop: `1px solid ${tokens.border.default}` }}>
            {authorities.map((auth, index) => {
              const displayName =
                AUTHORITY_DISPLAY_NAMES[auth.authority] || auth.authority;
              const color = chartColors[index % chartColors.length];
              const percentage =
                grandTotal > 0 ? (auth.totalDuty / grandTotal) * 100 : 0;

              return (
                <Box
                  key={auth.authority}
                  onClick={() => auth.codes.length > 0 && handleToggleExpand(auth.authority)}
                  sx={{
                    cursor: auth.codes.length > 0 ? "pointer" : "default",
                    py: { xs: 0.6, md: 0.75 },
                    borderBottom: `1px solid ${tokens.border.default}`,
                    transition: "all 0.2s ease",
                    ...(auth.codes.length > 0 && {
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.02)",
                      },
                    }),
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          background: color,
                          borderRadius: "50%",
                          flexShrink: 0,
                        }}
                      />
                      <Tooltip title={displayName} arrow placement="top" disableHoverListener={false}>
                        <Typography variant="body2" fontWeight={tokens.fontWeight.semibold} noWrap sx={{ minWidth: 0, fontSize: { xs: tokens.fontSize.base, md: tokens.fontSize.md } }}>
                          {displayName}
                        </Typography>
                      </Tooltip>
                      {showIeepaRefundAlert && isIeepaAuthority(auth.authority) && (
                        <Tooltip
                          title="Open IEEPA Refund tool"
                          arrow
                          placement="top"
                        >
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              goToRefundTool();
                            }}
                            sx={{
                              fontSize: 9,
                              px: 0.5,
                              py: 0.15,
                              bgcolor: "#e8f5e9",
                              color: "#2e7d32",
                              borderRadius: 0.75,
                              fontWeight: tokens.fontWeight.bold,
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                              border: "1px solid #a5d6a7",
                              flexShrink: 0,
                              lineHeight: 1.3,
                              transition: "all 0.15s",
                              "&:hover": {
                                bgcolor: "#c8e6c9",
                                borderColor: "#81c784",
                              },
                            }}
                          >
                            Refund opportunity
                          </Box>
                        </Tooltip>
                      )}
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Typography
                        variant="caption"
                        sx={{ color: tokens.text.secondary, minWidth: 38, textAlign: "right", fontSize: { xs: tokens.fontSize.xs, md: tokens.fontSize.base } }}
                      >
                        {percentage.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" fontWeight={tokens.fontWeight.bold} color={tokens.text.body} sx={{ minWidth: 70, textAlign: "right", fontSize: { xs: tokens.fontSize.base, md: tokens.fontSize.lg } }}>
                        ${numberWithCommas(auth.totalDuty)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}

            {authorities.length === 0 && !isLoading && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  color: tokens.text.secondary,
                }}
              >
                <Typography variant="body2">
                  No duty data available for this period
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    </Card>
  );
};

export default DutiesCard;
